<template>
    <div class="rounded-lg border-2 p-3 text-sm" :class="border">
        <div class="flex items-center gap-2 font-medium">
            <UIcon :name="kindIcon" class="size-4 shrink-0" />
            {{ operation.summary }}
        </div>

        <ul class="mt-2 list-disc ms-5 text-muted">
            <li v-for="(line, i) in operation.preview" :key="i"><AssistantText :text="line" /></li>
        </ul>

        <div v-if="decidable" class="mt-3 flex gap-2">
            <UButton
                :label="$t('assistant.confirm')"
                icon="i-lucide-check"
                size="sm"
                :loading="deciding"
                @click="emit('decide', true)"
            />
            <UButton
                :label="$t('assistant.decline')"
                icon="i-lucide-x"
                size="sm"
                color="neutral"
                variant="subtle"
                :disabled="deciding"
                @click="emit('decide', false)"
            />
        </div>

        <!-- The line that says what happened, and the one place the guest link an
             offer was just issued under is ever shown. It is followable and copyable
             (AssistantText.vue) rather than forty characters to select by hand. -->
        <p v-else-if="operation.status !== 'proposed'" class="mt-2 flex items-baseline gap-1 text-xs" :class="tone">
            <UIcon :name="icon" class="size-4 shrink-0 self-center" />
            <AssistantText :text="label" />
        </p>
    </div>
</template>

<script setup lang="ts">
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'

    import AssistantText from '@/components/AssistantText.vue'
    import { chatOperationResult } from '@/libs/chatResult'
    import type { IChatOperation } from '@/types/Chat'

    /**
     * One proposal: the preview the server built, and the two buttons that decide it.
     *
     * Lifted out of Assistant.vue so that a step of a plan and a lone proposal are drawn
     * by the same code. They were the same thing before plans existed and staying that
     * way is the point — the desk should not be able to tell which of the two it is
     * looking at.
     */

    const props = defineProps<{
        operation: IChatOperation
        /** Whether this card may be pressed at all: the reader's own thread, and — inside
         *  a plan — the step whose turn it is. */
        actionable: boolean
        deciding:   boolean
    }>()

    const emit = defineEmits<{ decide: [confirm: boolean] }>()

    const { t } = useI18n()

    const decidable = computed((): boolean => props.actionable && props.operation.status === 'proposed')

    /**
     * The card's own icon, and the only thing here that is decided by the KIND.
     *
     * What a proposal says stays the server's: `summary` and `preview` are built there
     * out of validated parameters, precisely so that a person never presses Confirm on
     * the model's prose, and the browser is not sent the payload at all (types/Chat.ts).
     * So a per-kind body would be a second description of a change, drawn from fields
     * this portal does not have — which is why the housekeeping team cards carry their
     * members, their factor, their day and their before → after in the preview lines,
     * exactly as the housekeeping plan's do.
     *
     * The icon is the one thing the kind can honestly decide. A team is a row about
     * PEOPLE and it is the only kind here that is, so it gets the Ekipe tab's own icon
     * (docs/HOUSEKEEPING.md §15.8) rather than the clipboard every other proposal wears
     * — a supervisor scrolling a morning's thread picks it out at a glance.
     *
     * The rooming kinds take the GROUP SCREEN's own two (views/group/Groups.vue): the
     * link its link button wears and the checklist its rooming button wears. They are
     * two rather than one because they are the two icons a receptionist already
     * associates with those two acts, and a link that looked like a list is the card
     * somebody presses expecting names to be written.
     */
    const kindIcon = computed((): string => {
        if (props.operation.kind === 'housekeeping_team' || props.operation.kind === 'housekeeping_team_change') {
            return 'i-lucide-users'
        }

        if (props.operation.kind === 'rooming_link') return 'i-lucide-link'

        // The guest's own portal link, sent on WhatsApp. A speech bubble and not the
        // chain beside it: what this card authorises is a MESSAGE leaving the building,
        // and the link is what it carries.
        if (props.operation.kind === 'guest_link_whatsapp') return 'i-lucide-message-circle'

        if (props.operation.kind === 'rooming_entries' || props.operation.kind === 'rooming_pending') {
            return 'i-lucide-list-checks'
        }

        // A correction to a guest's card is the only kind here about ONE PERSON's own
        // data, and the pen on the figure is what the guest screen's own edit button
        // wears - a receptionist scrolling a thread picks it out without reading.
        if (props.operation.kind === 'guest_update') return 'i-lucide-user-pen'

        return 'i-lucide-clipboard-check'
    })

    const border = computed((): string => {
        if (props.operation.status === 'proposed') return 'border-primary/60'
        if (props.operation.status === 'executed') return 'border-success/60'

        return 'border-accented'
    })

    const tone = computed((): string => {
        if (props.operation.status === 'executed') return 'text-success'

        return props.operation.status === 'failed' ? 'text-error' : 'text-muted'
    })

    const icon = computed((): string => {
        if (props.operation.status === 'executed') return 'i-lucide-circle-check'

        return props.operation.status === 'failed' ? 'i-lucide-circle-x' : 'i-lucide-ban'
    })

    // What the execution did comes from its structured outcome, translated here — never
    // from the sentence the service wrote for its own audit trail (libs/chatResult.ts).
    const label = computed((): string => {
        const result = chatOperationResult(props.operation)

        if (props.operation.status === 'executed') return t('assistant.executed') + (result ? ` — ${result}` : '')
        if (props.operation.status === 'failed') return t('assistant.op_failed') + (result ? ` — ${result}` : '')

        return t('assistant.declined')
    })
</script>
